// Loading a screen's data: { status: 'loading' | 'ready' | 'error', data, error, reload, setData }.
// error is an AdminError (src/admin/api.js), so error.message is the line to show. A load that
// finishes after the screen has moved on, or after a newer load started, is dropped.
//
//   const people = useLoad(() => api.staff_people({ search: q }), [q])
//   if (people.status === 'error') return <Problem error={people.error} onRetry={people.reload} />
import { useCallback, useEffect, useRef, useState } from 'react'
import { adminError } from '../api.js'

export function useLoad(loader, deps = []) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const latest = useRef(0)
  const loaderRef = useRef(loader)
  loaderRef.current = loader

  const load = useCallback(async ({ quiet = false } = {}) => {
    const n = ++latest.current
    if (!quiet) setState(s => ({ ...s, status: s.data === null ? 'loading' : s.status, error: null }))
    try {
      const data = await loaderRef.current()
      if (n === latest.current) setState({ status: 'ready', data, error: null })
      return data
    } catch (e) {
      if (n === latest.current) setState(s => ({ status: 'error', data: s.data, error: adminError(e) }))
      return null
    }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() ; return () => { latest.current++ } }, deps)

  const setData = useCallback(update => setState(s => ({ ...s, data: typeof update === 'function' ? update(s.data) : update })), [])
  const reload = useCallback(() => load({ quiet: true }), [load])
  return { ...state, reload, setData }
}

// A write with its own busy state and the line it answered with:
//   const save = useAction(body => api.grant_access(body))
//   <Button busy={save.busy} onClick={() => save.run(body).then(answer => ...)}>
//   {save.error && <p className="ad-error" role="alert">{save.error.message}</p>}
// run() resolves with the answer, or with null after a refusal (kept in error).
export function useAction(action) {
  const [state, setState] = useState({ busy: false, error: null, answer: null })
  const ref = useRef(action)
  ref.current = action
  const busy = useRef(false)
  const run = useCallback(async (...args) => {
    if (busy.current) return null
    busy.current = true
    setState({ busy: true, error: null, answer: null })
    try {
      const answer = await ref.current(...args)
      setState({ busy: false, error: null, answer })
      return answer
    } catch (e) {
      setState({ busy: false, error: adminError(e), answer: null })
      return null
    } finally {
      busy.current = false
    }
  }, [])
  const clear = useCallback(() => setState({ busy: false, error: null, answer: null }), [])
  return { ...state, run, clear }
}
